import type { ReportRead } from 'definitions/report-read';
import { cn } from 'helpers/cn';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { barWidth, challengerCount, formatDelta, formatRate, verdictSentence } from '@/state/experiments/report-card';
import { EXPECTATION_CHOICES } from '@/state/experiments/report-questions';
import type { ReportManager } from './ReportForm';

type ResultStepProps = {
  manager: ReportManager;
  experimentName: string;
  read: ReportRead | null;
  isLoading: boolean;
  isError: boolean;
};

const VERDICT_TONE = {
  winner: 'bg-ok-bg text-ok',
  loser: 'bg-danger-bg text-danger',
  inconclusive: 'bg-warn-bg text-warn',
} as const;

type ResultStripProps = { read: ReportRead };

/** The numbers the question is about, on screen while it is asked instead of recalled from memory. */
const ResultStrip = ({ read }: ResultStripProps) => (
  <div className="border-line rounded-card overflow-hidden border">
    <div className="bg-panel2 border-line text-dim flex items-center justify-between gap-3 border-b px-[13px] py-[9px]">
      <span className="micro-label">The result</span>
      <span className="micro-label">
        {read.days > 0 && `${read.days} ${read.days === 1 ? 'day' : 'days'} · `}
        {read.visitors.toLocaleString()} visitors
      </span>
    </div>

    <div className="flex flex-col gap-[9px] px-[13px] py-2.5">
      {read.variants.map((variant) => (
        <div
          key={variant.id}
          className="grid grid-cols-[1fr_auto_auto] items-center gap-2.5 text-meta md:grid-cols-[150px_54px_1fr_92px]"
        >
          <span className="text-foreground truncate">{variant.name}</span>
          <span className="num">{formatRate(variant.rate)}</span>
          <div className="bg-track rounded-pill hidden h-1.5 md:block">
            <div
              className={cn('h-1.5 rounded-pill', variant.isControl ? 'bg-bar-muted' : 'bg-accent')}
              style={{ width: `${barWidth(variant.rate, read.variants)}%` }}
            />
          </div>
          <span className="num text-dim text-right text-micro">{formatDelta(variant)}</span>
        </div>
      ))}
    </div>

    <div className={cn('border-line border-t px-[13px] py-[9px] text-meta', VERDICT_TONE[read.verdict.kind])}>
      {verdictSentence(read.verdict, challengerCount(read.variants))}
    </div>
  </div>
);

/**
 * Screen 1: the result first, then whether it held. "Did the result match your expectation" is
 * hard to answer from memory, so the numbers and the brief's own hypothesis sit above it. If the
 * read could not be loaded the question is still asked: the report is optional, and so is its
 * context.
 */
export const ResultStep = ({ manager, experimentName, read, isLoading, isError }: ResultStepProps) => {
  const { draft, setAnswer } = manager;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <DialogTitle className="text-foreground text-title font-medium">What happened with this test?</DialogTitle>
        <DialogDescription className="supporting">{experimentName}</DialogDescription>
      </div>

      {isLoading && <p className="supporting">Loading the result…</p>}
      {read && read.variants.length > 0 && <ResultStrip read={read} />}
      {/* Said out loud, so a missing table is never mistaken for a table that failed to draw. */}
      {read && read.variants.length === 0 && (
        <p className="supporting">No statistics are stored for this test yet, so there is no result table to show.</p>
      )}
      {isError && <p className="text-warn text-meta">Could not load the result. You can still answer below.</p>}

      {read?.hypothesis && (
        <div className="flex flex-col gap-1.5">
          <span className="micro-label text-dim">Your brief said</span>
          <p className="text-foreground text-body leading-[1.45]">{read.hypothesis}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <h3 className="micro-label text-foreground">Did that hold?</h3>
        <div role="radiogroup" aria-label="Did that hold?" className="grid grid-cols-2 gap-[7px]">
          {EXPECTATION_CHOICES.map((choice) => {
            const selected = draft.expectation === choice.value;
            return (
              <button
                key={choice.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setAnswer('expectation', selected ? null : choice.value)}
                className={cn(
                  'rounded-lg border px-3 py-[9px] text-left text-meta transition-colors duration-150',
                  'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-hidden',
                  selected ? 'border-sel-line bg-sel-bg' : 'border-input bg-panel hover:bg-hover',
                )}
              >
                {choice.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
